import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Package, Plus, Trash2 } from "lucide-react";

export const JobDetailPartsSection = ({
  job,
  parts,
  isMechanic,
  canEditCosts,
  labourCharge,
  setLabourCharge,
  selectedPart,
  setSelectedPart,
  partQuantity,
  setPartQuantity,
  partsDialogOpen,
  setPartsDialogOpen,
  onAddPart,
  onRemovePart,
  onUpdateLabourCharge,
}) => {
  // Don't show for mechanics
  if (isMechanic) return null;

  const handleAddPart = () => {
    if (!selectedPart) return;
    const part = parts.find((p) => p.id === selectedPart);
    if (!part) return;
    onAddPart({
      part_id: part.id,
      part_name: part.name,
      quantity: partQuantity,
      unit_price: part.unit_price,
    });
    setSelectedPart("");
    setPartQuantity(1);
    setPartsDialogOpen(false);
  };

  const partsTotal = (job.parts_used || []).reduce(
    (sum, p) => sum + p.quantity * p.unit_price,
    0
  );

  return (
    <Card className="border-slate-200 shadow-sm rounded-sm">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold text-slate-800 flex items-center gap-2">
            <Package className="w-5 h-5" />
            Parts & Labour
          </CardTitle>
          {canEditCosts && job.status !== "completed" && (
            <Dialog open={partsDialogOpen} onOpenChange={setPartsDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline" className="rounded-sm" data-testid="add-part-btn">
                  <Plus className="w-4 h-4 mr-1" />
                  Add Part
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-sm">
                <DialogHeader>
                  <DialogTitle>Add Part</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div>
                    <Label>Select Part</Label>
                    <Select value={selectedPart} onValueChange={setSelectedPart}>
                      <SelectTrigger className="rounded-sm">
                        <SelectValue placeholder="Choose a part" />
                      </SelectTrigger>
                      <SelectContent>
                        {parts.map((part) => (
                          <SelectItem key={part.id} value={part.id}>
                            {part.name} - ${part.unit_price.toFixed(2)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Quantity</Label>
                    <Input
                      type="number"
                      min="1"
                      value={partQuantity}
                      onChange={(e) => setPartQuantity(parseInt(e.target.value) || 1)}
                      className="rounded-sm"
                    />
                  </div>
                  <Button onClick={handleAddPart} className="w-full rounded-sm bg-blue-600 hover:bg-blue-700">
                    Add Part
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Labour Charge */}
        <div>
          <Label className="text-xs text-slate-500 uppercase font-semibold">Labour Charge</Label>
          {canEditCosts && job.status !== "completed" ? (
            <div className="flex items-center gap-2 mt-1">
              <span className="text-slate-500">$</span>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={labourCharge}
                onChange={(e) => setLabourCharge(parseFloat(e.target.value) || 0)}
                onBlur={onUpdateLabourCharge}
                className="w-32 rounded-sm"
                data-testid="labour-charge-input"
              />
            </div>
          ) : (
            <p className="text-slate-800 font-medium mt-1">
              ${(job.labour_charge || 0).toFixed(2)}
            </p>
          )}
        </div>

        {/* Parts List */}
        <div>
          <Label className="text-xs text-slate-500 uppercase font-semibold">Parts Used</Label>
          {(job.parts_used || []).length === 0 ? (
            <p className="text-slate-400 text-sm mt-1">No parts added</p>
          ) : (
            <div className="mt-2 space-y-2">
              {job.parts_used.map((part, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between p-2 bg-slate-50 rounded-sm"
                >
                  <div>
                    <p className="font-medium text-slate-800">{part.part_name}</p>
                    <p className="text-xs text-slate-500">
                      {part.quantity} × ${part.unit_price.toFixed(2)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">
                      ${(part.quantity * part.unit_price).toFixed(2)}
                    </Badge>
                    {canEditCosts && job.status !== "completed" && (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 text-red-500 hover:text-red-700"
                        onClick={() => onRemovePart(i)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
              <div className="flex justify-between pt-2 border-t">
                <span className="font-medium text-slate-600">Parts Total</span>
                <span className="font-bold text-slate-800">${partsTotal.toFixed(2)}</span>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default JobDetailPartsSection;
